import React, { useState } from "react";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import {
  LayoutDashboard,
  Users,
  ShoppingCart,
  Wallet,
  LogOut,
  Package,
  Store,
  Tag,
  Bell,
  FileText,
  HelpCircle,
  Truck,
  Layers,
  Zap,
  User,
  Settings,
} from "lucide-react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Outlet, useNavigate } from "react-router-dom";
import { Layout, Menu, Button, Badge, Avatar, Tooltip, Dropdown } from "antd";
import { AiOutlinePlus } from "react-icons/ai";

const { Header, Sider, Content } = Layout;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  const menuItems = [
    {
      key: "",
      icon: <LayoutDashboard size={20} />,
      label: "Dashboard",
    },
    {
      key: "customers",
      icon: <Users size={20} />,
      label: "Customers",
    },
    {
      key: "logistics",
      label: "Pharmacy Hubs",
      icon: <Truck size={20} />,
      children: [
        {
          key: "list-Store",
          icon: <Store size={18} />,
          label: "Hub Registry",
        },
        {
          key: "Add-Store",
          icon: <AiOutlinePlus size={18} />,
          label: "Register Hub",
        },
      ],
    },
    {
      key: "catalog",
      label: "Products",
      icon: <Store size={20} />,
      children: [
        { key: "product", icon: <Package size={18} />, label: "Add Product" },
        {
          key: "list-product",
          icon: <Layers size={18} />,
          label: "Product List",
        },
        { key: "brand", icon: <Tag size={18} />, label: "Add Brand" },
        {
          key: "list-brand",
          icon: <FileText size={18} />,
          label: "Brand List",
        },
        { key: "category", icon: <Tag size={18} />, label: "Add Category" },
        {
          key: "list-category",
          icon: <FileText size={18} />,
          label: "Category List",
        },
      ],
    },
    {
      key: "orders",
      icon: <ShoppingCart size={20} />,
      label: "Orders",
    },
    {
      key: "marketing",
      label: "Coupons",
      icon: <Wallet size={20} />,
      children: [
        { key: "coupon", icon: <Tag size={18} />, label: "Add Coupon" },
        {
          key: "coupon-list",
          icon: <FileText size={18} />,
          label: "Coupon List",
        },
      ],
    },
    {
      key: "blogs",
      label: "Articles & News",
      icon: <FileText size={20} />,
      children: [
        { key: "blog", icon: <FileText size={18} />, label: "Add Article" },
        { key: "blog-list", icon: <Layers size={18} />, label: "Article List" },
      ],
    },
    {
      key: "logistics_agents",
      label: "Delivery Fleet",
      icon: <Truck size={20} />,
      children: [
        {
          key: "createdelivery",
          icon: <Users size={18} />,
          label: "Register Agent",
        },
        {
          key: "delivery-boy-list",
          icon: <Layers size={18} />,
          label: "Agent Directory",
        },
      ],
    },
    {
      key: "enquiries",
      icon: <HelpCircle size={20} />,
      label: "Patient Inquiries",
    },
    {
      key: "care-suggestions",
      icon: <HelpCircle size={20} />,
      label: "Health Advice",
    },
    { type: "divider" },
    {
      key: "signout",
      icon: <LogOut size={20} className="text-rose-400" />,
      label: <span className="text-rose-400 font-semibold">Sign Out</span>,
      danger: true,
    },
  ];

  return (
    <Layout>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={280}
        collapsedWidth={80}
        className="fixed left-0 h-screen z-[100] border-r border-white/5"
        style={{
          background: "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)",
          boxShadow: "10px 0 50px -10px rgba(0,0,0,0.15)",
        }}
      >
        {/* Brand/Logo Section */}
        <div className="flex items-center gap-4 px-6 h-24 mb-4">
          <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-500/20 transform hover:scale-105 transition-transform cursor-pointer">
            <Zap size={24} fill="white" strokeWidth={0} />
          </div>
          {!collapsed && (
            <div className="animate-in fade-in slide-in-from-left-4 duration-300">
              <h1 className="text-white font-black text-[20px] leading-tight tracking-tight uppercase">
                Pulse <span className="text-blue-400">Addis</span>
              </h1>
              <p className="text-[10px] text-slate-400 font-black mt-0.5 uppercase tracking-[0.2em]">
                Health Console
              </p>
            </div>
          )}
        </div>

        {/* Navigation Area */}
        <div
          style={{
            height: "calc(100vh - 120px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
          className="sidebar-scroll pb-10"
        >
          <Menu
            theme="dark"
            mode="inline"
            defaultSelectedKeys={["dashboard"]}
            onClick={({ key }) => {
              if (key === "signout") {
                localStorage.clear();
                window.location.reload();
              } else {
                navigate(key);
              }
            }}
            className="px-3 sidebar-menu"
            style={{
              background: "transparent",
              border: "none",
            }}
            items={menuItems}
          />
        </div>
      </Sider>

      <Layout
        className="transition-all duration-300 min-h-screen bg-[#f8fafc]"
        style={{ marginLeft: collapsed ? 80 : 280 }}
      >
        <Header
          className="sticky top-0 z-50 flex justify-between items-center h-20 px-8 glass-header border-b border-slate-200/60"
          style={{ background: "rgba(255, 255, 255, 0.8)", backdropFilter: "blur(12px)" }}
        >
          <div className="flex items-center gap-6">
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              className="text-slate-400 hover:text-blue-600 w-11 h-11 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-100/50 hover:bg-blue-50 transition-all"
            />
            <div className="hidden lg:block relative">
               <span className="text-[14px] text-slate-400 font-bold uppercase tracking-widest pl-2">Pharmacy Dashboard</span>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Tooltip title="View Platform">
                <Button
                  type="text"
                  icon={<Store size={20} className="text-slate-400" />}
                  className="w-11 h-11 flex items-center justify-center rounded-xl hover:bg-slate-50"
                  onClick={() => window.open('http://localhost:3000', '_blank')}
                />
              </Tooltip>
              
              <Tooltip title="Notifications">
                <Badge count={8} size="small" offset={[-4, 4]}>
                  <Button
                    type="text"
                    icon={<Bell size={20} className="text-slate-400" />}
                    className="w-11 h-11 flex items-center justify-center rounded-xl hover:bg-slate-50"
                  />
                </Badge>
              </Tooltip>
            </div>

            <div className="h-8 w-[1px] bg-slate-200 mx-2" />

            <Dropdown
              menu={{
                items: [
                  {
                    key: "profile",
                    label: "Profile Settings",
                    icon: <User size={16} />,
                  },
                  {
                    key: "settings",
                    label: "Account Settings",
                    icon: <Settings size={16} />,
                  },
                  { type: "divider" },
                  {
                    key: "signout",
                    label: "Logout",
                    icon: <LogOut size={16} />,
                    danger: true,
                  },
                ],
                onClick: ({ key }) => {
                  if (key === "signout") {
                    localStorage.clear();
                    window.location.reload();
                  }
                },
              }}
              trigger={["click"]}
              placement="bottomRight"
              overlayClassName="header-dropdown"
            >
              <div className="flex items-center gap-4 cursor-pointer hover:bg-slate-50/50 p-1.5 rounded-2xl transition-all border border-transparent hover:border-slate-100 group">
                <Avatar
                  size={42}
                  className="rounded-xl border-2 border-white shadow-sm ring-1 ring-slate-100"
                  src="https://api.dicebear.com/7.x/avataaars/svg?seed=Felix&backgroundColor=f8fafc"
                />
                <div className="hidden sm:block">
                  <p className="text-[15px] font-black text-slate-900 leading-none group-hover:text-blue-600 transition-colors">Admin Hub</p>
                  <p className="text-[12px] text-slate-400 font-bold mt-1 tracking-wide">Administrator</p>
                </div>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="p-10 bg-white min-h-[calc(100vh-80px)]">
          <div className="max-w-[1400px] mx-auto">
            <Outlet />
          </div>
        </Content>
      </Layout>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        theme="light"
      />

      {/* Sidebar styles */}
      <style>{`
        .sidebar-menu .ant-menu-submenu-title,
        .sidebar-menu .ant-menu-item {
          height: 44px !important;
          line-height: 44px !important;
          margin: 2px 0 !important;
          border-radius: 10px !important;
          font-size: 15px !important;
          font-weight: 500 !important;
        }
        .sidebar-menu .ant-menu-sub.ant-menu-inline {
          background: rgba(0, 0, 0, 0.15) !important;
          border-radius: 10px !important;
          padding: 4px !important;
          margin: 4px 8px !important;
        }
        .sidebar-menu .ant-menu-sub .ant-menu-item {
          height: 38px !important;
          line-height: 38px !important;
          padding-left: 40px !important;
          font-size: 14px !important;
          margin: 1px 0 !important;
          border-radius: 8px !important;
        }
        .sidebar-menu .ant-menu-item-selected {
          background: rgba(37, 124, 183, 0.3) !important;
        }
        .sidebar-menu .ant-menu-submenu-arrow {
          color: rgba(255, 255, 255, 0.5) !important;
        }
        .sidebar-menu .ant-menu-item:hover,
        .sidebar-menu .ant-menu-submenu-title:hover {
          background: rgba(255, 255, 255, 0.08) !important;
        }
        .sidebar-menu .ant-menu-item-divider {
          border-color: rgba(255, 255, 255, 0.08) !important;
          margin: 12px 16px !important;
        }
        .sidebar-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .sidebar-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-scroll::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        @media (max-width: 768px) {
          .ant-layout-sider {
            position: fixed !important;
            z-index: 1000 !important;
          }
        }
        .settings-modal .ant-modal-content {
          border-radius: 24px !important;
          padding: 32px !important;
        }
        .header-dropdown .ant-dropdown-menu {
          padding: 8px !important;
          border-radius: 16px !important;
          border: 1px solid #f1f5f9 !important;
          box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1) !important;
        }
        .header-dropdown .ant-dropdown-menu-item {
          border-radius: 8px !important;
          padding: 10px 16px !important;
          font-weight: 500 !important;
          color: #1e293b !important;
        }
        .header-dropdown .ant-dropdown-menu-item:hover {
          background: #f8fafc !important;
        }
      `}</style>
    </Layout>
  );
};

export default MainLayout;
